import React, { useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import { track } from '../api/analytics';
import {
  saveLearningProfile,
  YEAR_LEVELS,
  NAPLAN_YEARS,
  COMING_SOON_EXAMS,
  pathwaysForYear,
  EXAM_META,
  Role,
  TargetExam,
  Goal,
  Cadence,
  Confidence,
} from '../api/onboarding';

// ── Brand palette (mirrors StudentDashboard.css) ────────────────────────────
const C = {
  green: '#5DAA3A',
  orange: '#F58720',
  grape: '#7C2BB8',
  coral: '#E26B4F',
  yellow: '#F4C534',
  ink: '#1A2A1F',
  muted: '#6b7280',
  border: '#e5e7eb',
  soft: '#f6f8f5',
};

const GOALS: { value: Goal; label: string; emoji: string }[] = [
  { value: 'prepare', label: 'Prepare for the test', emoji: '🎯' },
  { value: 'confidence', label: 'Build confidence', emoji: '💪' },
  { value: 'weak_area', label: 'Strengthen a weak area', emoji: '🔧' },
  { value: 'keep_sharp', label: 'Keep sharp', emoji: '✨' },
];

const CADENCES: { value: Cadence; label: string; meta: string }[] = [
  { value: 'daily_few', label: 'A few each day', meta: '~15 min / day' },
  { value: 'weekly_tests', label: 'Weekly practice tests', meta: '1–2 / week' },
  { value: 'intensive', label: 'Intensive prep', meta: 'Exam is close' },
];

// Focus areas: live (numeracy, language_conventions) + coming-soon.
const FOCUS_AREAS: {
  value: string;
  label: string;
  live: boolean;
  tone: string;
}[] = [
  { value: 'numeracy', label: 'Numeracy', live: true, tone: C.orange },
  {
    value: 'language_conventions',
    label: 'Language Conventions',
    live: true,
    tone: C.green,
  },
  { value: 'reading', label: 'Reading', live: false, tone: C.grape },
  { value: 'writing', label: 'Writing', live: false, tone: C.coral },
];

const CONFIDENCE_OPTS: { value: Confidence; label: string }[] = [
  { value: 'needs_work', label: 'Needs work' },
  { value: 'okay', label: 'Okay' },
  { value: 'strong', label: 'Strong' },
];

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

interface OnboardingWizardProps {
  /** Called after the profile is successfully saved. */
  onComplete: () => void;
}

const card: React.CSSProperties = {
  border: `1.5px solid ${C.border}`,
  borderRadius: 16,
  padding: '16px 18px',
  background: '#fff',
  cursor: 'pointer',
  textAlign: 'left',
  width: '100%',
  transition: 'all .15s ease',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
};

function selectableStyle(active: boolean, tone = C.green): React.CSSProperties {
  return {
    ...card,
    borderColor: active ? tone : C.border,
    background: active ? `${tone}10` : '#fff',
    boxShadow: active ? `0 0 0 2px ${tone}33` : 'none',
  };
}

// ── Adaptive-loop sell (ROO-141) ────────────────────────────────────────────
// Copy + a 3-step strip shown once, on the wizard's opening step, so the
// value of the loop is clear before we ask for any details. Pure intro — the
// existing steps and their analytics events are untouched.
const LOOP_STEPS: { emoji: string; label: string; tone: string }[] = [
  { emoji: '🎯', label: 'Quick placement quiz', tone: C.orange },
  { emoji: '🧭', label: 'Your personalised plan', tone: C.grape },
  { emoji: '📈', label: 'Practice that targets exactly what your child needs', tone: C.green },
];

const LoopIntro: React.FC = () => (
  <div style={{ textAlign: 'center', marginBottom: 22 }}>
    <h2
      style={{
        fontSize: 19,
        fontWeight: 800,
        margin: '0 0 14px',
        lineHeight: 1.35,
      }}
    >
      Quick placement quiz &rarr; your personalised plan &rarr; practice that
      targets exactly what your child needs.
    </h2>
    <div
      style={{
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'center',
        gap: 6,
        flexWrap: 'wrap',
      }}
    >
      {LOOP_STEPS.map((s, i) => (
        <React.Fragment key={s.label}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 4,
              width: 108,
              padding: '10px 8px',
              borderRadius: 14,
              background: `${s.tone}12`,
              border: `1.5px solid ${s.tone}33`,
            }}
          >
            <span style={{ fontSize: 20 }} aria-hidden="true">{s.emoji}</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: C.ink, lineHeight: 1.25 }}>
              {s.label}
            </span>
          </div>
          {i < LOOP_STEPS.length - 1 && (
            <span
              aria-hidden="true"
              style={{
                alignSelf: 'center',
                fontSize: 16,
                fontWeight: 800,
                color: C.muted,
                margin: '0 2px',
              }}
            >
              &rarr;
            </span>
          )}
        </React.Fragment>
      ))}
    </div>
  </div>
);

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ onComplete }) => {
  const { user } = useAuth();
  const studentId = user?.id || user?.studentId || '';
  const isChildDefault = false;

  const [step, setStep] = useState(0); // 0,1,2
  const [submitting, setSubmitting] = useState(false);

  // Step 1
  const [role, setRole] = useState<Role | null>(isChildDefault ? 'child' : null);
  const [studentName, setStudentName] = useState(
    isChildDefault ? '' : user?.name?.split(' ')[0] || ''
  );
  const [yearLevel, setYearLevel] = useState<string>('');

  // Step 2
  const [targetExam, setTargetExam] = useState<TargetExam | null>(null);
  const [goal, setGoal] = useState<Goal | null>(null);
  const [targetMonth, setTargetMonth] = useState<number | null>(null); // 0–11
  const [notSureDate, setNotSureDate] = useState(false);

  // Step 3
  const [focusAreas, setFocusAreas] = useState<string[]>([]);
  const [confidence, setConfidence] = useState<Record<string, Confidence>>({});
  const [cadence, setCadence] = useState<Cadence | null>(null);
  const [remindersOptIn, setRemindersOptIn] = useState(true);

  // "My child" → second-person about the child; "Myself" → first person.
  const isChild = role === 'child';
  const youName = studentName.trim() || (isChild ? 'your child' : 'you');

  const pathways = useMemo(
    () => (yearLevel ? pathwaysForYear(yearLevel) : []),
    [yearLevel]
  );

  // When year changes, drop a target exam that's no longer valid.
  React.useEffect(() => {
    if (targetExam && !pathways.includes(targetExam)) {
      setTargetExam(null);
    }
  }, [pathways, targetExam]);

  // Fire onboarding_step_viewed whenever a step renders (1-indexed step number).
  // Fail-soft: track() never throws.
  useEffect(() => {
    void track('onboarding_step_viewed', {
      userId: studentId || undefined,
      properties: { step: step + 1 },
    });
  }, [step, studentId]);

  const liveFocus = focusAreas.filter((f) =>
    FOCUS_AREAS.find((x) => x.value === f && x.live)
  );

  // ── Per-step validation ────────────────────────────────────────────────
  const step1Valid = !!role && !!studentName.trim() && !!yearLevel;
  const step2Valid = !!targetExam && !!goal;
  const step3Valid =
    focusAreas.length >= 1 &&
    !!cadence &&
    // every selected LIVE area needs a confidence rating
    liveFocus.every((f) => !!confidence[f]);

  const stepValid = [step1Valid, step2Valid, step3Valid][step];

  const toggleFocus = (value: string) => {
    setFocusAreas((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleNext = () => {
    if (!stepValid) return;
    if (step < 2) {
      void track('onboarding_step_completed', {
        userId: studentId || undefined,
        properties: { step: step + 1 },
      });
      setStep(step + 1);
    }
  };
  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleFinish = async () => {
    if (!step3Valid || !studentId) return;
    setSubmitting(true);

    // Step 3 advances via submit (not handleNext), so fire its completion here.
    void track('onboarding_step_completed', {
      userId: studentId || undefined,
      properties: { step: 3 },
    });

    try {
      const targetDate =
        notSureDate || targetMonth == null
          ? null
          : (() => {
              // Next occurrence of the chosen month, first day.
              const now = new Date();
              let year = now.getFullYear();
              if (targetMonth < now.getMonth()) year += 1;
              const mm = String(targetMonth + 1).padStart(2, '0');
              return `${year}-${mm}-01`;
            })();

      // Confidence only for live, selected areas.
      const cleanConfidence: Record<string, Confidence> = {};
      liveFocus.forEach((f) => {
        if (confidence[f]) cleanConfidence[f] = confidence[f];
      });

      const saved = await saveLearningProfile(studentId, {
        role: role as Role,
        studentName: studentName.trim(),
        yearLevel,
        targetExam: targetExam as TargetExam,
        goal: goal as Goal,
        targetDate,
        focusAreas,
        confidence: cleanConfidence,
        cadence: cadence as Cadence,
        remindersOptIn,
      });

      // Funnel completion — fail-soft, must not block the success flow.
      void track('onboarding_completed', {
        userId: studentId || undefined,
        properties: {
          role,
          yearLevel,
          recommendedBand: saved?.recommendedBand ?? null,
          targetExam,
          goal,
          focusAreas,
          cadence,
        },
      });

      message.success("You're all set!");
      onComplete();
    } catch (err: any) {
      console.error('Failed to save onboarding profile', err);
      message.error(
        err?.response?.data?.error || 'Could not save — please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ── Progress dots ●──○──○ ──────────────────────────────────────────────
  const Progress = () => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        margin: '4px 0 22px',
      }}
    >
      {[0, 1, 2].map((i) => (
        <React.Fragment key={i}>
          <span
            aria-label={`Step ${i + 1}`}
            style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              background: i <= step ? C.green : '#d6ddd2',
              boxShadow: i === step ? `0 0 0 4px ${C.green}22` : 'none',
              transition: 'all .2s',
            }}
          />
          {i < 2 && (
            <span
              style={{
                width: 28,
                height: 3,
                borderRadius: 2,
                background: i < step ? C.green : '#d6ddd2',
              }}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const titles = [
    isChild ? "Who's learning?" : 'Tell us about you',
    'Your goal & target',
    'Focus & cadence',
  ];
  const subtitles = [
    'A couple of quick details to personalise everything.',
    isChild
      ? `What is ${youName} working towards?`
      : 'What are you working towards?',
    'Where to focus and how often to practise.',
  ];

  return (
    <div
      style={{
        minHeight: '100vh',
        background: `linear-gradient(180deg, ${C.soft} 0%, #fff 40%)`,
        padding: '24px 16px 48px',
        fontFamily:
          '"Bricolage Grotesque", system-ui, -apple-system, sans-serif',
        color: C.ink,
      }}
    >
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        {/* Sell the adaptive loop up front (ROO-141) — intro only, step 1 only. */}
        {step === 0 && <LoopIntro />}

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: C.green,
            }}
          >
            Step {step + 1} of 3
          </div>
        </div>
        <Progress />

        <div
          style={{
            background: '#fff',
            border: `1px solid ${C.border}`,
            borderRadius: 22,
            padding: '26px 22px',
            boxShadow: '0 18px 40px -24px rgba(20,42,31,.35)',
          }}
        >
          <h1
            style={{
              fontSize: 24,
              fontWeight: 800,
              margin: '0 0 4px',
              lineHeight: 1.2,
            }}
          >
            {titles[step]}
          </h1>
          <p style={{ color: C.muted, margin: '0 0 22px', fontSize: 15 }}>
            {subtitles[step]}
          </p>

          {/* ── STEP 1 ─────────────────────────────────────────────── */}
          {step === 0 && (
            <div style={{ display: 'grid', gap: 18 }}>
              <Field label="Who is learning?">
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    type="button"
                    style={{ ...selectableStyle(role === 'self', C.green), justifyContent: 'center' }}
                    onClick={() => setRole('self')}
                  >
                    🙋 Myself
                  </button>
                  <button
                    type="button"
                    style={{ ...selectableStyle(role === 'child', C.grape), justifyContent: 'center' }}
                    onClick={() => setRole('child')}
                  >
                    👶 My child
                  </button>
                </div>
              </Field>

              <Field label={isChild ? "Child's first name" : 'Your first name'}>
                <input
                  value={studentName}
                  onChange={(e) => setStudentName(e.target.value)}
                  placeholder={isChild ? 'e.g. Mia' : 'e.g. Alex'}
                  style={inputStyle}
                />
              </Field>

              <Field label="Year level">
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(74px, 1fr))',
                    gap: 8,
                  }}
                >
                  {YEAR_LEVELS.map((y) => {
                    const active = yearLevel === y.value;
                    const naplan = NAPLAN_YEARS.has(y.value);
                    return (
                      <button
                        key={y.value}
                        type="button"
                        onClick={() => setYearLevel(y.value)}
                        style={{
                          position: 'relative',
                          padding: '12px 6px',
                          borderRadius: 12,
                          border: `1.5px solid ${active ? C.green : C.border}`,
                          background: active ? `${C.green}12` : '#fff',
                          boxShadow: active ? `0 0 0 2px ${C.green}33` : 'none',
                          cursor: 'pointer',
                          fontWeight: 700,
                          fontSize: 13,
                          color: C.ink,
                        }}
                      >
                        {y.value === 'K' ? 'Kindy' : `Yr ${y.value}`}
                        {naplan && (
                          <span
                            style={{
                              position: 'absolute',
                              top: -7,
                              right: -6,
                              background: C.yellow,
                              color: C.ink,
                              fontSize: 8,
                              fontWeight: 800,
                              padding: '2px 5px',
                              borderRadius: 999,
                              letterSpacing: '0.04em',
                            }}
                          >
                            NAPLAN
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </Field>
            </div>
          )}

          {/* ── STEP 2 ─────────────────────────────────────────────── */}
          {step === 1 && (
            <div style={{ display: 'grid', gap: 20 }}>
              <Field label="Target pathway">
                <div style={{ display: 'grid', gap: 10 }}>
                  {pathways.map((ex) => {
                    const active = targetExam === ex;
                    const soon = COMING_SOON_EXAMS.has(ex);
                    const meta = EXAM_META[ex];
                    return (
                      <button
                        key={ex}
                        type="button"
                        onClick={() => setTargetExam(ex)}
                        style={selectableStyle(active, C.orange)}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                            {meta.label}
                            {soon && (
                              <span
                                style={{
                                  background: '#fff4e0',
                                  color: C.orange,
                                  border: `1px solid ${C.orange}55`,
                                  fontSize: 10,
                                  fontWeight: 800,
                                  padding: '2px 7px',
                                  borderRadius: 999,
                                }}
                              >
                                Coming soon
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 13, color: C.muted }}>
                            {meta.blurb}
                          </div>
                        </div>
                        <span style={{ fontSize: 18, color: active ? C.orange : '#cbd5cf' }}>
                          {active ? '●' : '○'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </Field>

              <Field label="What's the goal?">
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 10,
                  }}
                >
                  {GOALS.map((g) => (
                    <button
                      key={g.value}
                      type="button"
                      onClick={() => setGoal(g.value)}
                      style={{ ...selectableStyle(goal === g.value, C.grape) }}
                    >
                      <span style={{ fontSize: 20 }}>{g.emoji}</span>
                      <span style={{ fontWeight: 600, fontSize: 14 }}>{g.label}</span>
                    </button>
                  ))}
                </div>
              </Field>

              <Field label="Target month (optional)">
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(6, 1fr)',
                    gap: 6,
                    opacity: notSureDate ? 0.45 : 1,
                    pointerEvents: notSureDate ? 'none' : 'auto',
                  }}
                >
                  {MONTHS.map((m, i) => {
                    const active = targetMonth === i;
                    return (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setTargetMonth(active ? null : i)}
                        style={{
                          padding: '8px 0',
                          borderRadius: 10,
                          border: `1.5px solid ${active ? C.coral : C.border}`,
                          background: active ? `${C.coral}12` : '#fff',
                          cursor: 'pointer',
                          fontWeight: 700,
                          fontSize: 12,
                        }}
                      >
                        {m}
                      </button>
                    );
                  })}
                </div>
                <label
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    marginTop: 10,
                    fontSize: 14,
                    cursor: 'pointer',
                    color: C.muted,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={notSureDate}
                    onChange={(e) => {
                      setNotSureDate(e.target.checked);
                      if (e.target.checked) setTargetMonth(null);
                    }}
                  />
                  Not sure yet
                </label>
              </Field>
            </div>
          )}

          {/* ── STEP 3 ─────────────────────────────────────────────── */}
          {step === 2 && (
            <div style={{ display: 'grid', gap: 20 }}>
              <Field label="Focus areas (pick at least one)">
                <div style={{ display: 'grid', gap: 10 }}>
                  {FOCUS_AREAS.map((f) => {
                    const active = focusAreas.includes(f.value);
                    return (
                      <div key={f.value}>
                        <button
                          type="button"
                          onClick={() => toggleFocus(f.value)}
                          style={selectableStyle(active, f.tone)}
                        >
                          <span
                            style={{
                              width: 22,
                              height: 22,
                              borderRadius: 6,
                              border: `2px solid ${active ? f.tone : '#cbd5cf'}`,
                              background: active ? f.tone : '#fff',
                              color: '#fff',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 13,
                              fontWeight: 800,
                            }}
                          >
                            {active ? '✓' : ''}
                          </span>
                          <span style={{ flex: 1, fontWeight: 700 }}>{f.label}</span>
                          {!f.live && (
                            <span
                              style={{
                                background: '#fff4e0',
                                color: C.orange,
                                border: `1px solid ${C.orange}55`,
                                fontSize: 10,
                                fontWeight: 800,
                                padding: '2px 7px',
                                borderRadius: 999,
                              }}
                            >
                              Coming soon
                            </span>
                          )}
                        </button>

                        {/* Confidence selector for selected LIVE areas */}
                        {active && f.live && (
                          <div
                            style={{
                              display: 'flex',
                              gap: 6,
                              margin: '8px 0 0 8px',
                            }}
                          >
                            {CONFIDENCE_OPTS.map((c) => {
                              const csel = confidence[f.value] === c.value;
                              return (
                                <button
                                  key={c.value}
                                  type="button"
                                  onClick={() =>
                                    setConfidence((prev) => ({
                                      ...prev,
                                      [f.value]: c.value,
                                    }))
                                  }
                                  style={{
                                    flex: 1,
                                    padding: '7px 4px',
                                    borderRadius: 9,
                                    border: `1.5px solid ${csel ? f.tone : C.border}`,
                                    background: csel ? `${f.tone}12` : '#fff',
                                    cursor: 'pointer',
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: C.ink,
                                  }}
                                >
                                  {c.label}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Field>

              <Field label="How often?">
                <div style={{ display: 'grid', gap: 10 }}>
                  {CADENCES.map((c) => (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => setCadence(c.value)}
                      style={selectableStyle(cadence === c.value, C.green)}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700 }}>{c.label}</div>
                        <div style={{ fontSize: 13, color: C.muted }}>{c.meta}</div>
                      </div>
                      <span style={{ fontSize: 18, color: cadence === c.value ? C.green : '#cbd5cf' }}>
                        {cadence === c.value ? '●' : '○'}
                      </span>
                    </button>
                  ))}
                </div>
              </Field>

              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  fontSize: 14,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={remindersOptIn}
                  onChange={(e) => setRemindersOptIn(e.target.checked)}
                />
                Send {isChild ? 'me' : 'me'} a gentle weekly reminder &amp; progress
                summary
              </label>
            </div>
          )}

          {/* ── Controls ───────────────────────────────────────────── */}
          <div
            style={{
              display: 'flex',
              gap: 12,
              marginTop: 26,
              alignItems: 'center',
            }}
          >
            {step > 0 && (
              <button
                type="button"
                onClick={handleBack}
                disabled={submitting}
                style={{
                  padding: '13px 20px',
                  borderRadius: 14,
                  border: `1.5px solid ${C.border}`,
                  background: '#fff',
                  fontWeight: 700,
                  cursor: 'pointer',
                  color: C.ink,
                }}
              >
                Back
              </button>
            )}
            <button
              type="button"
              onClick={step < 2 ? handleNext : handleFinish}
              disabled={!stepValid || submitting}
              style={{
                flex: 1,
                padding: '13px 20px',
                borderRadius: 14,
                border: 'none',
                background: stepValid ? C.green : '#c7d2cc',
                color: '#fff',
                fontWeight: 800,
                fontSize: 16,
                cursor: stepValid && !submitting ? 'pointer' : 'not-allowed',
                transition: 'background .15s',
              }}
            >
              {step < 2
                ? 'Continue'
                : submitting
                ? 'Saving…'
                : 'Finish & go to dashboard'}
            </button>
          </div>
        </div>

        <p style={{ textAlign: 'center', color: C.muted, fontSize: 12, marginTop: 16 }}>
          This takes about a minute and helps us tailor your practice.
        </p>
      </div>
    </div>
  );
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '13px 14px',
  borderRadius: 12,
  border: `1.5px solid ${C.border}`,
  fontSize: 15,
  outline: 'none',
  fontFamily: 'inherit',
  color: C.ink,
  boxSizing: 'border-box',
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div>
    <div
      style={{
        fontSize: 13,
        fontWeight: 800,
        color: C.ink,
        marginBottom: 8,
        letterSpacing: '0.01em',
      }}
    >
      {label}
    </div>
    {children}
  </div>
);

export default OnboardingWizard;
